import React, { useEffect, useState } from 'react';
import { TransferProofService, TransferProof } from '../services/transfer-proof.service';
import { useNotification } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import styles from './AdminTransferProofsPage.module.css';

export default function AdminTransferProofsPage() {
  const [proofs, setProofs] = useState<TransferProof[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('PENDING');
  const [selectedProof, setSelectedProof] = useState<TransferProof | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const apiBase =
    import.meta.env.VITE_API_URL ||
    `${window.location.protocol}//${window.location.hostname}:3000/api`;
  const baseUrl = apiBase.replace('/api', '');
  const buildFileUrl = (fileUrl?: string | null) => (fileUrl ? `${baseUrl}${fileUrl}` : '');

  useEffect(() => {
    loadProofs();
  }, [filter]);

  useEffect(() => {
    if (!selectedProof?.fileUrl || selectedProof.mimeType !== 'application/pdf') {
      setPdfPreviewUrl(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const loadPdf = async () => {
      try {
        const response = await fetch(buildFileUrl(selectedProof.fileUrl), { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setPdfPreviewUrl(objectUrl);
        }
      } catch (error) {
        if (!cancelled) {
          setPdfPreviewUrl(null);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedProof?.id, selectedProof?.fileUrl, selectedProof?.mimeType]);

  const loadProofs = async () => {
    setLoading(true);
    try {
      const res = await TransferProofService.getAll({ status: filter });
      setProofs(res.data);
    } catch (error: any) {
      addNotification(t('transfer_proofs.review_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedProof || !reviewAction) return;

    if (reviewAction === 'REJECTED' && !rejectionReason.trim()) {
      addNotification(t('common.required_field_missing'), 'info');
      return;
    }

    setSubmitting(true);
    try {
      await TransferProofService.review(selectedProof.id, {
        status: reviewAction,
        rejectionReason: reviewAction === 'REJECTED' ? rejectionReason : undefined,
        adminNotes: adminNotes || undefined,
      });

      addNotification(
        reviewAction === 'APPROVED' ? t('transfer_proofs.approve_success') : t('transfer_proofs.reject_success'),
        'success'
      );
      
      setSelectedProof(null);
      setReviewAction(null);
      setRejectionReason('');
      setAdminNotes('');
      loadProofs();
    } catch (error: any) {
      addNotification(error?.message || t('transfer_proofs.review_error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewModal = (proof: TransferProof) => {
    setSelectedProof(proof);
    setReviewAction(null);
    setRejectionReason('');
    setAdminNotes('');
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { text: string; className: string }> = {
      PENDING: { text: t('transfer_proofs.status_pending'), className: styles.statusPending },
      APPROVED: { text: t('transfer_proofs.status_approved'), className: styles.statusApproved },
      REJECTED: { text: t('transfer_proofs.status_rejected'), className: styles.statusRejected },
    };
    return badges[status] || { text: status, className: '' };
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('transfer_proofs.admin_title')}</h1>
        <p className={styles.subtitle}>
          {t('transfer_proofs.admin_subtitle')}
        </p>
      </div>

      <div className={styles.filters}>
        <button
          className={filter === 'PENDING' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('PENDING')}
        >
          {t('transfer_proofs.filter_pending')} ({proofs.length})
        </button>
        <button
          className={filter === 'APPROVED' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('APPROVED')}
        >
          {t('transfer_proofs.filter_approved')}
        </button>
        <button
          className={filter === 'REJECTED' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('REJECTED')}
        >
          {t('transfer_proofs.filter_rejected')}
        </button>
      </div>

      {proofs.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>✅</div>
          <h2>{t('transfer_proofs.no_proofs')}</h2>
          <p>
            {filter === 'PENDING'
              ? t('transfer_proofs.no_proofs_hint')
              : `No hay comprobantes ${filter === 'APPROVED' ? t('transfer_proofs.status_approved').toLowerCase() : t('transfer_proofs.status_rejected').toLowerCase()}.`}
          </p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('transfer_proofs.table_date')}</th>
                <th>{t('common.user')}</th>
                <th>{t('transfer_proofs.table_project')}</th>
                <th>{t('transfer_proofs.table_amount')}</th>
                <th>{t('transfer_proofs.table_status')}</th>
                <th>{t('transfer_proofs.table_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {proofs.map((proof) => {
                const badge = getStatusBadge(proof.status);
                return (
                  <tr key={proof.id}>
                    <td className={styles.idColumn}>#{proof.id}</td>
                    <td>{new Date(proof.createdAt).toLocaleDateString('es-MX')}</td>
                    <td>
                      {proof.user?.name || 'N/A'}
                      <br />
                      <small className={styles.email}>{proof.user?.email}</small>
                    </td>
                    <td>{proof.development?.title || `ID: ${proof.developmentId}`}</td>
                    <td className={styles.amount}>
                      ${Number(proof.amount).toLocaleString('es-MX')} {proof.currency}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${badge.className}`}>
                        {badge.text}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.btnReview}
                        onClick={() => openReviewModal(proof)}
                      >
                        {proof.status === 'PENDING' ? t('common.review') : t('common.view_details')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de revisión */}
      {selectedProof && (
        <div className={styles.modalOverlay} onClick={() => !submitting && setSelectedProof(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.closeBtn}
              onClick={() => !submitting && setSelectedProof(null)}
              disabled={submitting}
            >
              ✕
            </button>

            <h2 className={styles.modalTitle}>{t('transfer_proofs.review_modal_title')}</h2>

            <div className={styles.modalContent}>
              <div className={styles.proofInfo}>
                <p>
                  <strong>{t('common.user')}:</strong> {selectedProof.user?.name || 'N/A'}
                </p>
                <p>
                  <strong>{t('profile.email_label')}:</strong> {selectedProof.user?.email || 'N/A'}
                </p>
                <p>
                  <strong>{t('transfer_proofs.table_project')}:</strong> {selectedProof.development?.title || `ID: ${selectedProof.developmentId}`}
                </p>
                <p>
                  <strong>{t('transfer_proofs.table_amount')}:</strong> ${Number(selectedProof.amount).toLocaleString('es-MX')} {selectedProof.currency}
                </p>
                <p>
                  <strong>{t('transfer_proofs.table_date')}:</strong> {
                    (() => {
                      try {
                        const date = new Date(selectedProof.createdAt);
                        return date.toLocaleString('es-MX');
                      } catch (error) {
                        return selectedProof.createdAt || 'Fecha inválida';
                      }
                    })()
                  }
                </p>
                <p>
                  <strong>ID:</strong> #{selectedProof.id}
                </p>
              </div>

              <div className={styles.filePreview}>
                <h3>{t('transfer_proofs.file_label')}</h3>
                
                {selectedProof.fileUrl && (
                  <>
                    {selectedProof.mimeType === 'application/pdf' ? (
                      pdfPreviewUrl ? (
                        <iframe
                          src={pdfPreviewUrl}
                          className={styles.pdfViewer}
                          title="Comprobante PDF"
                        />
                      ) : (
                        <div className={styles.pdfViewer}>Cargando PDF...</div>
                      )
                    ) : (
                      <img
                        src={buildFileUrl(selectedProof.fileUrl)}
                        alt="Comprobante"
                        className={styles.imageViewer}
                        onError={(e) => {
                          console.error('Error loading image:', selectedProof.fileUrl);
                          console.error('Full URL attempted:', buildFileUrl(selectedProof.fileUrl));
                          (e.target as HTMLImageElement).style.display = 'none';
                          const errorDiv = document.createElement('div');
                          errorDiv.innerHTML = `<p style="color: red; text-align: center; padding: 20px;">Error al cargar la imagen</p>`;
                          (e.target as HTMLImageElement).parentNode?.appendChild(errorDiv);
                        }}
                        onLoad={() => console.log('Image loaded successfully:', buildFileUrl(selectedProof.fileUrl))}
                      />
                    )}
                    
                    <div className={styles.downloadSection}>
                      <button
                        className={styles.downloadButton}
                        onClick={async () => {
                          try {
                            const fullUrl = buildFileUrl(selectedProof.fileUrl);
                            const response = await fetch(fullUrl, { credentials: 'include' });
                            if (!response.ok) {
                              throw new Error(`HTTP ${response.status}`);
                            }
                            const blob = await response.blob();
                            const objectUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = objectUrl;
                            link.download = selectedProof.fileName || 'comprobante';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(objectUrl);
                          } catch (error) {
                            console.error('Error downloading file:', error);
                            addNotification('No se pudo descargar el archivo', 'error');
                          }
                        }}
                      >
                        💾 Descargar archivo
                      </button>
                    </div>
                  </>
                )}
                
                {!selectedProof.fileUrl && (
                  <div className={styles.noFile}>
                    <p>No se pudo cargar la vista previa del archivo</p>
                    <p>URL: {selectedProof.fileUrl}</p>
                  </div>
                )}
              </div>

              {selectedProof.status === 'PENDING' && (
                <>
                  <div className={styles.actionButtons}>
                    <button
                      className={`${styles.btnAction} ${styles.btnApprove} ${reviewAction === 'APPROVED' ? styles.btnActive : ''}`}
                      onClick={() => setReviewAction('APPROVED')}
                      disabled={submitting}
                    >
                      ✅ {t('transfer_proofs.approve_button')}
                    </button>
                    <button
                      className={`${styles.btnAction} ${styles.btnReject} ${reviewAction === 'REJECTED' ? styles.btnActive : ''}`}
                      onClick={() => setReviewAction('REJECTED')}
                      disabled={submitting}
                    >
                      ❌ {t('transfer_proofs.reject_button')}
                    </button>
                  </div>

                  {reviewAction === 'REJECTED' && (
                    <div className={styles.formGroup}>
                      <label htmlFor="rejectionReason">
                        {t('transfer_proofs.rejection_reason_label')} <span className={styles.required}>*</span>
                      </label>
                      <textarea
                        id="rejectionReason"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder={t('transfer_proofs.rejection_reason_label') + '...'}
                        className={styles.textarea}
                        rows={4}
                        disabled={submitting}
                      />
                    </div>
                  )}

                  <div className={styles.formGroup}>
                    <label htmlFor="adminNotes">{t('transfer_proofs.admin_notes_label')} ({t('common.optional')})</label>
                    <textarea
                      id="adminNotes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder={t('transfer_proofs.admin_notes_label') + '...'}
                      className={styles.textarea}
                      rows={3}
                      disabled={submitting}
                    />
                  </div>

                  <div className={styles.modalActions}>
                    <button
                      className={styles.btnCancel}
                      onClick={() => setSelectedProof(null)}
                      disabled={submitting}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      className={styles.btnSubmit}
                      onClick={handleReview}
                      disabled={!reviewAction || submitting}
                    >
                      {submitting ? t('common.processing') + '...' : t('common.confirm')}
                    </button>
                  </div>
                </>
              )}

              {selectedProof.status !== 'PENDING' && (
                <div className={styles.reviewInfo}>
                  <p>
                    <strong>{t('common.reviewed_by')}:</strong> {selectedProof.reviewer?.name || 'N/A'}
                  </p>
                  <p>
                    <strong>{t('common.review_date')}:</strong>{' '}
                    {selectedProof.reviewedAt
                      ? new Date(selectedProof.reviewedAt).toLocaleString('es-MX')
                      : 'N/A'}
                  </p>
                  {selectedProof.rejectionReason && (
                    <p>
                      <strong>{t('transfer_proofs.rejection_reason_label')}:</strong> {selectedProof.rejectionReason}
                    </p>
                  )}
                  {selectedProof.adminNotes && (
                    <p>
                      <strong>{t('common.notes')}:</strong> {selectedProof.adminNotes}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
